import { Link } from "react-router-dom";
import { ArrowRight, Cable, Truck, PencilRuler, BatteryCharging, Phone, CheckCircle2, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { SectionHeading } from "../components/SectionHeading";
import { BrandLogo, IRVE_BRANDS } from "../components/BrandLogo";
import { FacebookFeed } from "../components/FacebookFeed";
import { NewsletterSection } from "../components/NewsletterSection";
import { RealisationsSection } from "../components/RealisationsSection";

const HERO_BG = "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?w=2000&q=80";

const activities = [
  {
    icon: Cable,
    title: "Électrotechnique",
    desc: "Coffrets de comptage provisoires, installations électriques BTP, conformité EDF.",
    href: "/electrotechnique",
    iconBg: "from-[#1B3A6B] to-[#0F76B8]",
    iconColor: "text-white",
  },
  {
    icon: BatteryCharging,
    title: "IRVE — Bornes de recharge",
    desc: "Installation, maintenance et mise en service de bornes pour véhicules électriques.",
    href: "/irve",
    iconBg: "from-[#3DAE2B] to-[#2d8420]",
    iconColor: "text-white",
  },
  {
    icon: Truck,
    title: "Location de matériel",
    desc: "Nacelles, groupes électrogènes, échafaudages, compresseurs… livraison rapide sur site.",
    href: "/location",
    iconBg: "from-[#FBBF24] to-[#F59E0B]",
    iconColor: "text-[#1B3A6B]",
  },
  {
    icon: PencilRuler,
    title: "Plans & DAO",
    desc: "Plans électriques, permis de construire (<150 m²), plans de fabrication, assainissement.",
    href: "/dao",
    iconBg: "from-slate-700 to-slate-900",
    iconColor: "text-[#FBBF24]",
  },
];

const certifications = [
  "Technicien IRVE Niveau 1 & 2",
  "Qualifié Qualifelec",
  "Conformité EDF",
  "Interventions toute l'île de La Réunion",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/85 to-[#0F172A]/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-medium mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FBBF24] animate-pulse" />
              Basé à Sainte-Suzanne · Toute l'île de La Réunion
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight leading-[1.05]">
              L'expertise électrotechnique
              <span className="block text-[#FBBF24]">au service de vos chantiers</span>
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              INGélec, votre partenaire en prestations électrotechniques, location de matériel BTP et études techniques DAO.
              Une expertise de proximité, des solutions sur mesure.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-semibold rounded-full px-7 h-12 text-base shadow-lg shadow-yellow-500/20" data-testid="hero-cta-contact">
                  Demander un devis <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/location">
                <Button size="lg" variant="outline" className="rounded-full px-7 h-12 text-base bg-white/5 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-[#1B3A6B]" data-testid="hero-cta-catalog">
                  Voir le catalogue
                </Button>
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3">
              {certifications.map((c) => (
                <div key={c} className="flex items-center gap-2 text-sm text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-[#FBBF24]" /> {c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ACTIVITIES BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <SectionHeading
          eyebrow="Nos 4 pôles d'activité"
          title="Une expertise complète pour vos projets"
          subtitle="De l'étude technique à la mise en service, INGélec vous accompagne sur l'ensemble de la chaîne électrotechnique"
          testId="activities-heading"
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {activities.map((a) => (
            <Link
              key={a.title}
              to={a.href}
              data-testid={`activity-card-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white hover:shadow-[0_20px_50px_-15px_rgba(27,58,107,0.25)] hover:-translate-y-1 transition-all duration-300 p-8"
            >
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${a.iconBg} ${a.iconColor} mb-5 shadow-lg`}>
                <a.icon className="w-7 h-7" strokeWidth={2} />
              </div>
              <h3 className="font-display text-2xl font-bold text-slate-900 tracking-tight">{a.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{a.desc}</p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3A6B] group-hover:gap-3 transition-all">
                Découvrir <ArrowRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* STATS / TRUST STRIP */}
      <section className="bg-[#1B3A6B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { num: "Niveau 1 & 2", label: "Installateur qualifié IRVE" },
              { num: "100%", label: "Île de La Réunion" },
              { num: "<48h", label: "Devis personnalisé" },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-display text-3xl sm:text-4xl font-bold text-[#FBBF24]">{s.num}</div>
                <div className="mt-2 text-sm text-slate-300">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS & CERT STRIP */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-1 flex md:justify-start justify-center">
              <img src="https://customer-assets.emergentagent.com/job_services-electro-dao/artifacts/z838tcki_logo-IRVE-L500.jpg" alt="Qualifelec IRVE" className="h-24 w-auto" data-testid="home-qualifelec-logo" />
            </div>
            <div className="md:col-span-4">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4 text-center md:text-left">Qualifié IRVE · Partenaires marques de référence</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-center">
                {IRVE_BRANDS.map((name) => (
                  <div key={name} className="flex items-center justify-center px-4 py-4 bg-white rounded-lg border border-slate-200 min-h-[80px]" data-testid={`home-brand-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <BrandLogo name={name} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RÉALISATIONS */}
      <RealisationsSection />

      {/* FACEBOOK FEED */}
      <FacebookFeed />

      {/* NEWSLETTER */}
      <NewsletterSection />

      {/* CONTACT CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0F172A] to-[#1B3A6B] p-10 lg:p-16">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#FBBF24]/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Un projet ? Parlons-en
            </h2>
            <p className="mt-4 text-slate-300 text-lg leading-relaxed">
              Devis gratuit sous 48h. Conseil personnalisé selon votre chantier, votre besoin et votre budget.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="tel:0692371331">
                <Button size="lg" className="bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-semibold rounded-full px-7 h-12" data-testid="home-cta-call">
                  <Phone className="w-4 h-4 mr-2" /> 0692 37 13 31
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="rounded-full px-7 h-12 bg-transparent border-white/40 text-white hover:bg-white hover:text-[#1B3A6B]" data-testid="home-cta-form">
                  Formulaire de contact
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-slate-400">
              <MapPin className="w-4 h-4" /> Sainte-Suzanne — Intervention sur toute l'île
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
